import { Forward30, Pause, PlayArrow, Replay30 } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Theme } from '@mui/material';
import { useSelector } from 'react-redux';

import { PLAYER_FORWARD_30, PLAYER_REPLAY_30 } from '@/constants';
import { useDebouncedValue } from '@/hooks';
import {
  playerThunks,
  selectIsChangingChapter,
  selectIsPlayerBuffering,
  selectIsPlayerPlaying,
  selectIsPlayerSeeking,
  selectPlayerProgress,
  useAppDispatch,
} from '@/store';
import { Chapter } from '@/types';

interface MediaControlsProps {
  chapter?: Chapter;
  setSeekPosition: (position: { position: number }) => void;
}

export const MediaControls = ({
  chapter,
  setSeekPosition,
}: MediaControlsProps) => {
  const dispatch = useAppDispatch();

  const isBuffering = useSelector(selectIsPlayerBuffering);
  const isSeeking = useSelector(selectIsPlayerSeeking);
  const isPlaying = useSelector(selectIsPlayerPlaying);
  const isChangingChapter = useSelector(selectIsChangingChapter);
  const { position } = useSelector(selectPlayerProgress);

  const showLoadingIndicator = useDebouncedValue(
    isBuffering || isChangingChapter,
    1000,
  );

  const togglePlay = () => {
    dispatch(
      isPlaying ? playerThunks.pausePlayer() : playerThunks.playPlayer(),
    );
  };

  const seekEpisode = (delta: number) => {
    const seekToPosition = Math.max(
      Math.min(position! + delta, chapter!.duration),
      1,
    );

    setSeekPosition({ position: seekToPosition });
  };

  return (
    <Box sx={styles.mediaControlsContainer}>
      <IconButton
        sx={styles.iconButton}
        onClick={() => seekEpisode(PLAYER_REPLAY_30)}
        disabled={isSeeking}
        aria-label="Back 30 seconds">
        <Replay30 sx={styles.icon} />
      </IconButton>

      {showLoadingIndicator ? (
        <CircularProgress sx={styles.circularProgressIndicator} />
      ) : (
        <IconButton
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? (
            <Pause sx={[styles.playPauseIcon, styles.iconSelected]} />
          ) : (
            <PlayArrow sx={[styles.playPauseIcon, styles.iconSelected]} />
          )}
        </IconButton>
      )}

      <IconButton
        sx={styles.iconButton}
        onClick={() => seekEpisode(PLAYER_FORWARD_30)}
        disabled={isSeeking}
        aria-label="Forward 30 seconds">
        <Forward30 sx={styles.icon} />
      </IconButton>
    </Box>
  );
};

const styles = {
  mediaControlsContainer: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    margin: '0 auto',
  },
  iconButton: {
    height: 56,
    width: 56,
  },
  playPauseIcon: (theme: Theme) => ({
    fontSize: 60,
    [theme.breakpoints.down('sm')]: {
      fontSize: 40,
    },
  }),
  icon: {
    fontSize: 30,
    color: 'grey100',
  },
  activeIcon: {
    color: 'secondary.active',
  },
  iconSelected: {
    color: 'secondary.active',
  },
  playEpisodeButton: {
    color: 'secondary.active',
    fontSize: 18,
  },
  circularProgressIndicator: {
    color: 'secondary.active',
    fontSize: 32,
  },
};
